import { LockSimpleIcon } from "@phosphor-icons/react";
import {
  formatTemplateStudioComponentLabel,
  getTemplateStudioComponentStructureDescription,
  resolveTemplateStudioStructureIcon,
} from "@/lib/template-studio/templateStudioComponentDisplay";
import { cn } from "@/lib/utils";

const formatStructureOrderLabel = (order: number) =>
  String(order).padStart(2, "0");

export interface TemplatePreviewStructureRowProps {
  order: number;
  /** json-render element `type` from the saved template spec (Template Studio registry key). */
  componentType: string;
  /** When set, shown instead of the catalog-derived component label (e.g. section title). */
  title?: string;
  /** When set, shown instead of the catalog-derived structure description. */
  description?: string;
  className?: string;
}

/** Read-only structure row with lock affordance for template preview. */
export const TemplatePreviewStructureRow = ({
  order,
  componentType,
  title,
  description,
  className,
}: TemplatePreviewStructureRowProps) => {
  const BlockIcon = resolveTemplateStudioStructureIcon(componentType);
  const resolvedTitle =
    title ?? formatTemplateStudioComponentLabel(componentType);
  const resolvedDescription =
    description ??
    getTemplateStudioComponentStructureDescription(componentType);

  return (
    <div
      className={cn(
        "relative flex min-h-16 gap-3 border-b border-gray-200 bg-white px-3 py-3 last:border-b-0",
        className,
      )}
    >
      <div
        aria-hidden
        className="flex size-6 shrink-0 items-center justify-center rounded-md bg-gray-100 p-1.5"
      >
        <BlockIcon className="size-3 text-gray-700" weight="regular" />
      </div>

      <div className="min-w-0 flex-1 pr-8">
        <div className="flex min-w-0 items-center gap-2">
          <p className="text-sm font-medium leading-5 text-gray-900">
            {resolvedTitle}
          </p>
          <LockSimpleIcon
            aria-hidden
            className="size-2.5 shrink-0 text-gray-500"
            weight="fill"
          />
        </div>
        <p className="text-xs leading-5 tracking-[0.12px] text-gray-600">
          {resolvedDescription}
        </p>
      </div>

      <p
        aria-hidden
        className="absolute top-3 right-3 text-xs leading-5 tracking-[0.12px] text-gray-400"
      >
        {formatStructureOrderLabel(order)}
      </p>
    </div>
  );
};
